// src/shared/api/api-client.ts
import { env } from '@/config/env';
import { useAuthStore } from '@/auth/auth-store';

export class ApiError extends Error {
  status: number;
  body: string;
  constructor(status: number, body: string) {
    super(`API Error ${status}: ${body}`);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

export interface ApiCallOptions {
  signal?: AbortSignal;
  /**
   * Per-call practice scope for the `PracticeId` header. Defaults to the authenticated user's
   * login practice. Pass the TARGET practice for calls that act on a practice other than the
   * login default — e.g. booking an appointment into a selected practice (Flutter sends the
   * booking practice in this header, not the login default). Absent → login practice, as before.
   */
  practiceId?: string;
  /**
   * Send with `fetch` `keepalive`, so the request survives the page going away. Set only by the saves a page fires as it
   * is hidden or unloaded (a refresh, a tab close): without it the browser may cancel a write it started in `pagehide`,
   * and the edit is lost (dev repro, 10 Oct 2026). Best effort: a body over the page's keepalive budget goes out as an
   * ordinary request, and a keepalive PUT / DELETE the browser refuses is sent again without it (`apiClient`).
   */
  keepalive?: boolean;
  /** Extra request headers (`If-Match`, `Idempotency-Key`). The client's own headers (auth, app, practice) are set after these. */
  headers?: HeadersInit;
}

/**
 * The keepalive budget. Browsers cap the bodies of ALL keepalive requests in flight from a page at 64 KB together, and
 * over the cap `fetch` throws a TypeError instead of sending. 60 KB leaves headroom; the note and the Fall / Pain saves of
 * one page-hide flush draw on the same budget. Measured on dev (frame v2): a note PUT runs 0.4–7.6 KB, and each completed
 * phrase adds about 6 KB, so a long note can pass it — that one is sent without keepalive.
 */
export const KEEPALIVE_BUDGET_BYTES = 60 * 1024;
let keepaliveInFlightBytes = 0;

/** Reserve `body` against the keepalive budget. Returns a release, or null when it does not fit. */
function reserveKeepalive(body: BodyInit | null | undefined): (() => void) | null {
  if (body !== undefined && body !== null && typeof body !== 'string') return null; // only JSON text is measured
  const bytes = body ? new TextEncoder().encode(body).length : 0;
  if (keepaliveInFlightBytes + bytes > KEEPALIVE_BUDGET_BYTES) return null;
  keepaliveInFlightBytes += bytes;
  let released = false;
  return () => {
    if (released) return;
    released = true;
    keepaliveInFlightBytes -= bytes;
  };
}

/**
 * The app-surface discriminator every Scrips backend receives from this client — as the `AppType`
 * header here, and as the `appType` query parameter on the ambient WebSocket, which cannot carry
 * a header. It must match what Identity was sent at login (`auth/token-service.ts`): Identity
 * compares a presented token against the session row stored for that app type.
 */
export const APP_TYPE = 'Provider';

function buildHeaders(
  token: string,
  extras?: HeadersInit,
  isMultipart = false,
  practiceIdOverride?: string,
): Headers {
  const { user } = useAuthStore.getState();
  const headers = new Headers(extras);
  if (!isMultipart) headers.set('Content-Type', 'application/json');
  headers.set('Authorization', `Bearer ${token}`);
  headers.set('AppType', APP_TYPE);
  headers.set('userTimeZone', Intl.DateTimeFormat().resolvedOptions().timeZone);
  // Org/practice/user context — STRICTLY from the authenticated user (HIPAA guard). PracticeId
  // honors a per-call override (the target practice), falling back to the login practice.
  if (user?.organizationId) headers.set('OrganizationId', user.organizationId);
  const practiceId = practiceIdOverride ?? user?.practiceId;
  if (practiceId) headers.set('PracticeId', practiceId);
  if (user?.userId) headers.set('UserId', user.userId);
  if (user?.role) headers.set('RoleId', user.role);
  return headers;
}

async function parse<T>(res: Response): Promise<T> {
  if (!res.ok) throw new ApiError(res.status, await res.text());
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

// PROD-1817: exported (PM exports it too) so ported PM modules that call the
// low-level client directly compile. Same function, unchanged behaviour —
// only its visibility changed.
export async function apiClient<T>(
  endpoint: string,
  options: RequestInit = {},
  ctx: { practiceId?: string; baseUrl?: string } = {},
): Promise<T> {
  const { getValidAccessToken, forceRefresh } = useAuthStore.getState();
  // Base host defaults to the PM-API; callers targeting another Scrips service
  // (e.g. Scrips.Workflows on a separate host) pass ctx.baseUrl. All auth,
  // refresh-on-401 and HIPAA context headers are shared regardless of host.
  const url = `${ctx.baseUrl ?? env.apiBaseUrl}${endpoint}`;
  const isMultipart = options.body instanceof FormData;

  // Keepalive is best effort (see `ApiCallOptions.keepalive`): within the page's budget, or not at all.
  const { keepalive: wantKeepalive, ...init } = options;
  const release = wantKeepalive ? reserveKeepalive(init.body) : null;
  const send = async (token: string): Promise<Response> => {
    const headers = buildHeaders(token, init.headers, isMultipart, ctx.practiceId);
    if (!release) return fetch(url, { ...init, headers });
    try {
      return await fetch(url, { ...init, headers, keepalive: true });
    } catch (e) {
      // A TypeError may be the browser refusing the keepalive request (over its own cap, or a policy) — or a network /
      // CORS failure AFTER the server committed. So it is sent again the ordinary way, once, only when sending twice is
      // harmless: an idempotent PUT / DELETE. A POST is never re-sent (a second create would duplicate the record).
      const method = (init.method ?? 'GET').toUpperCase();
      if (!(e instanceof TypeError) || init.signal?.aborted || (method !== 'PUT' && method !== 'DELETE')) throw e;
      return fetch(url, { ...init, headers });
    }
  };

  try {
    let res = await send(await getValidAccessToken());
    if (res.status === 401) {
      res = await send(await forceRefresh());
    }
    return await parse<T>(res);
  } finally {
    release?.();
  }
}

export const get = <T>(endpoint: string, opts: ApiCallOptions = {}) =>
  apiClient<T>(
    endpoint,
    { method: 'GET', signal: opts.signal, ...(opts.headers ? { headers: opts.headers } : {}) },
    { practiceId: opts.practiceId },
  );

/**
 * GET against the Scrips.Workflows service (separate host from the PM-API).
 * Same auth / refresh-on-401 / HIPAA context-header machinery as `get`, but the
 * request is routed to `env.workflowsBaseUrl` (empty in local dev → the Vite
 * `/workflows` proxy forwards to dev-workflows). Used by the appointment
 * care-pathway feed (PROD-738). Kept as a thin wrapper so there is exactly one
 * copy of the fetch/auth logic.
 */
export const getWorkflows = <T>(endpoint: string, opts: ApiCallOptions = {}) =>
  apiClient<T>(
    endpoint,
    { method: 'GET', signal: opts.signal },
    { practiceId: opts.practiceId, baseUrl: env.workflowsBaseUrl },
  );

/** POST against Scrips.Workflows — the `getWorkflows` host and machinery, for a write. */
export const postWorkflows = <T>(endpoint: string, body: unknown, opts: ApiCallOptions = {}) =>
  apiClient<T>(
    endpoint,
    { method: 'POST', body: JSON.stringify(body), signal: opts.signal },
    { practiceId: opts.practiceId, baseUrl: env.workflowsBaseUrl },
  );

/**
 * POST against Scrips.VideoApplication, which hosts the ambient scribe (PROD-1979).
 *
 * A third host, on the `getWorkflows` pattern above and for the same reason: the ambient service
 * is not routed under `VITE_API_BASE_URL`, so a plain `post` would send the body to the PM-API
 * and collect a 404. Everything that makes a call authenticated and HIPAA-scoped — bearer token,
 * refresh-on-401, Organization/Practice/User headers — is shared, because there is exactly one
 * copy of it.
 *
 * NOTE ON CORS. The practitioner origins (`localhost:5173` and the `*-practitioner-v2` hosts) are
 * in Scrips.VideoApplication's allowlist (`Startup.cs`, PROD-1980); an origin missing from it
 * fails at the preflight. Deliberately no Vite proxy entry and no `mode: 'no-cors'`: a missing
 * allowlist entry has to fail loudly at the network layer, where it is diagnosable, rather than be
 * tunnelled around in the client and rediscovered in production. The caller surfaces it — see
 * `encounter/ambient/request-reanalysis.ts`.
 */
export const postAmbient = <T>(endpoint: string, body: unknown, opts: ApiCallOptions = {}) =>
  apiClient<T>(
    endpoint,
    { method: 'POST', body: JSON.stringify(body), signal: opts.signal },
    { practiceId: opts.practiceId, baseUrl: env.ambientBaseUrl.replace(/\/$/, '') },
  );

export const post = <T>(endpoint: string, body: unknown, opts: ApiCallOptions = {}) =>
  apiClient<T>(
    endpoint,
    {
      method: 'POST',
      body: JSON.stringify(body),
      signal: opts.signal,
      ...(opts.keepalive ? { keepalive: true } : {}),
      ...(opts.headers ? { headers: opts.headers } : {}),
    },
    { practiceId: opts.practiceId },
  );

export const put = <T>(endpoint: string, body: unknown, opts: ApiCallOptions = {}) =>
  apiClient<T>(
    endpoint,
    {
      method: 'PUT',
      body: JSON.stringify(body),
      signal: opts.signal,
      ...(opts.keepalive ? { keepalive: true } : {}),
      ...(opts.headers ? { headers: opts.headers } : {}),
    },
    { practiceId: opts.practiceId },
  );

export const del = <T>(endpoint: string, body?: unknown, opts: ApiCallOptions = {}) =>
  apiClient<T>(endpoint, {
    method: 'DELETE',
    body: body === undefined ? undefined : JSON.stringify(body),
    signal: opts.signal,
  }, { practiceId: opts.practiceId });

export const patch = <T>(endpoint: string, body?: unknown, opts: ApiCallOptions = {}) =>
  apiClient<T>(endpoint, {
    method: 'PATCH',
    body: body === undefined ? undefined : JSON.stringify(body),
    signal: opts.signal,
  }, { practiceId: opts.practiceId });

export const postForm = <T>(endpoint: string, body: FormData, opts: ApiCallOptions = {}) =>
  apiClient<T>(endpoint, { method: 'POST', body, signal: opts.signal }, { practiceId: opts.practiceId });

/**
 * POST that returns the response body as raw text — used by endpoints that
 * return a bare string OR ship JSON under a `text/plain` content-type
 * (e.g. Scheduling `/BlockSlot` / `/UnblockSlot`). Same auth + refresh-on-401
 * machinery as `apiClient`, just no JSON.parse.
 *
 * Ported from dev-scrips-pm-react src/shared/api/api-client.ts
 * (PROD-1190 calendar port — required by use-save-block-slot.ts /
 * use-unblock-slot.ts). Adapted to this repo's api-client machinery
 * (env.apiBaseUrl + 3-arg buildHeaders); behaviour matches the PM original.
 */
export async function postText(
  endpoint: string,
  body: unknown,
  opts: ApiCallOptions = {},
): Promise<string> {
  const { getValidAccessToken, forceRefresh } = useAuthStore.getState();
  const url = `${env.apiBaseUrl}${endpoint}`;

  const send = (token: string): Promise<Response> =>
    fetch(url, {
      method: 'POST',
      body: JSON.stringify(body),
      headers: buildHeaders(token, undefined, false, opts.practiceId),
      signal: opts.signal,
    });

  let res = await send(await getValidAccessToken());
  if (res.status === 401) {
    res = await send(await forceRefresh());
  }
  if (!res.ok) throw new ApiError(res.status, await res.text());
  return res.text();
}

/**
 * Multipart POST that returns the response body as raw text — used by
 * endpoints that accept a `FormData` body AND return a bare string
 * (e.g. UploadPatientDocument returns the signed blob URL as plain
 * text). Same auth + refresh-on-401 machinery as `apiClient`, just no
 * JSON.parse and the body is FormData so the browser sets the
 * multipart boundary automatically.
 *
 * Ported from dev-scrips-pm-react src/shared/api/api-client.ts
 * (PROD-1119 documents module — required by use-upload-document.ts).
 * Adapted to this repo's api-client machinery (env.apiBaseUrl +
 * 3-arg buildHeaders); behaviour is identical to the PM original.
 */
export async function postFormText(
  endpoint: string,
  body: FormData,
  opts: ApiCallOptions = {},
): Promise<string> {
  const { getValidAccessToken, forceRefresh } = useAuthStore.getState();
  const url = `${env.apiBaseUrl}${endpoint}`;

  const send = (token: string): Promise<Response> =>
    fetch(url, {
      method: 'POST',
      body,
      headers: buildHeaders(token, undefined, true, opts.practiceId),
      signal: opts.signal,
    });

  let res = await send(await getValidAccessToken());
  if (res.status === 401) {
    res = await send(await forceRefresh());
  }
  if (!res.ok) throw new ApiError(res.status, await res.text());
  return res.text();
}

/**
 * GET that returns the response body as a Blob — used for binary fetches
 * (the blank consent-template PDF pulled into the fill-and-sign canvas).
 * Same auth + refresh-on-401 machinery as `apiClient`, just `res.blob()`
 * instead of a JSON parse.
 *
 * Ported from dev-scrips-pm-react src/shared/api/api-client.ts
 * (PROD-1051 consent doc-forms — required by consent/lib/pdf.ts). Adapted to
 * this repo's api-client machinery (env.apiBaseUrl + 4-arg buildHeaders, no
 * getClientIpAddress); behaviour matches the PM original.
 */
export async function getBlob(
  endpoint: string,
  opts: ApiCallOptions = {},
): Promise<Blob> {
  const { getValidAccessToken, forceRefresh } = useAuthStore.getState();
  const url = `${env.apiBaseUrl}${endpoint}`;

  const send = (token: string): Promise<Response> =>
    fetch(url, {
      headers: buildHeaders(token, undefined, false, opts.practiceId),
      signal: opts.signal,
    });

  let res = await send(await getValidAccessToken());
  if (res.status === 401) {
    res = await send(await forceRefresh());
  }
  if (!res.ok) throw new ApiError(res.status, await res.text());
  return res.blob();
}

/**
 * Request that returns the raw `Response`, for callers that need a response HEADER as well as the body: the Document
 * Intake review routes return the run's `ETag` and take it back as `If-Match` (PROD-3204). Same auth +
 * refresh-on-401 machinery as `apiClient`. It does not throw on a non-2xx status; the caller reads it.
 */
export async function fetchWithAuth(
  endpoint: string,
  init: RequestInit = {},
  opts: ApiCallOptions = {},
): Promise<Response> {
  const { getValidAccessToken, forceRefresh } = useAuthStore.getState();
  const url = `${env.apiBaseUrl}${endpoint}`;
  const isMultipart = init.body instanceof FormData;

  const send = (token: string): Promise<Response> =>
    fetch(url, {
      ...init,
      headers: buildHeaders(token, init.headers, isMultipart, opts.practiceId),
      signal: opts.signal ?? init.signal,
    });

  let res = await send(await getValidAccessToken());
  if (res.status === 401) {
    res = await send(await forceRefresh());
  }
  return res;
}

/**
 * The full header set (`buildHeaders`) for a request this client cannot send itself: an `XMLHttpRequest` upload,
 * which `fetch` cannot report progress for (Document Intake batch upload, PROD-3204). Pass `refresh: true` after a
 * 401 to get a refreshed token.
 */
export async function authHeaders(
  opts: { multipart?: boolean; practiceId?: string; refresh?: boolean } = {},
): Promise<Headers> {
  const { getValidAccessToken, forceRefresh } = useAuthStore.getState();
  const token = opts.refresh ? await forceRefresh() : await getValidAccessToken();
  return buildHeaders(token, undefined, opts.multipart ?? false, opts.practiceId);
}
